import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { client } from "@/data/client";
import { FunnelViz } from "@/components/FunnelViz";
import { MultiSeriesChart } from "@/components/MultiSeriesChart";
import { formatBRL, formatPct } from "@/lib/utils";
import { playPop } from "@/lib/sound";
import { SellerPerformance } from "@/pages/dashboard-sections/SellerPerformance";
import { CloserStageSection } from "@/pages/dashboard-sections/CloserStageSection";
import type { CloserStageKey } from "@/data/types";

type Period = { periodStart: string; periodEnd: string };

const SERIES = [
  { key: "meetingsScheduled", name: "Reuniões agendadas", color: "hsl(var(--chart-4))" },
  { key: "meetingsHeld", name: "Reuniões realizadas", color: "hsl(var(--chart-3))" },
  { key: "sales", name: "Vendas", color: "hsl(var(--chart-1))" },
];

export function CloserFunnelTab({ period }: { period: Period }) {
  const [selectedStage, setSelectedStage] = useState<CloserStageKey | null>(null);

  const kpis = useQuery({ queryKey: ["closer-funnel-kpis", period], queryFn: () => client.getCloserFunnelKpis(period) });
  const daily = useQuery({ queryKey: ["closer-funnel-daily", period], queryFn: () => client.getCloserFunnelDaily(period) });

  const k = kpis.data;

  const handleStageClick = (stage: CloserStageKey) => {
    playPop();
    setSelectedStage(stage);
  };

  return (
    <div className="space-y-6">
      <div
        className="grid gap-5"
        style={{
          gridTemplateColumns: selectedStage
            ? "minmax(0, 2fr) minmax(0, 3fr)"
            : "minmax(0, 1fr)",
          transition: "grid-template-columns 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <div className="min-w-0 min-h-0">
          <FunnelViz<CloserStageKey>
            title="Funil de fechamento"
            description="Fechamento · da reunião agendada ao valor gerado · taxa de conversão entre cada par · clique para ver os contatos"
            stages={[
              {
                key: "meetingsScheduled",
                label: "Reuniões agendadas",
                value: k?.meetingsScheduled ?? 0,
                caption: "calendário confirmado",
                color: "var(--chart-4)",
              },
              {
                key: "meetingsHeld",
                label: "Reuniões realizadas",
                value: k?.meetingsHeld ?? 0,
                caption: "show rate",
                color: "var(--chart-3)",
              },
              {
                key: "sales",
                label: "Vendas",
                value: k?.sales ?? 0,
                caption: "negócios fechados",
                color: "var(--chart-1)",
              },
              {
                key: "valueGenerated",
                label: "Valor gerado",
                value: k?.valueGenerated ?? 0,
                displayValue: k ? formatBRL(k.valueGenerated) : "—",
                caption: "receita do período",
                color: "var(--chart-2)",
              },
            ]}
            rates={[
              k ? formatPct(k.scheduledToHeldRate, 1) : "—",
              k ? formatPct(k.heldToSalesRate, 1) : "—",
              k ? (k.sales ? formatBRL(Math.round(k.valueGenerated / k.sales)) + "/venda" : "—") : "—",
            ]}
            activeStage={selectedStage}
            onStageClick={handleStageClick}
          />
        </div>

        <AnimatePresence mode="wait">
          {selectedStage && (
            <motion.div
              key={selectedStage}
              initial={{ opacity: 0, x: 28 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 28 }}
              transition={{ duration: 0.35, ease: [0.215, 0.61, 0.355, 1] }}
              className="min-w-0"
            >
              <CloserStageSection
                stage={selectedStage}
                period={period}
                onClear={() => setSelectedStage(null)}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <MultiSeriesChart
        data={daily.data ?? []}
        series={SERIES}
        title="Visão geral"
        subtitle="Evolução do fechamento por dia"
        defaultMode="line"
        allowModeToggle={false}
        pulseAllSeries
      />

      <SellerPerformance period={period} />
    </div>
  );
}
